/**
 * wellnessApi.js — API functions for the Wellness Report page.
 *
 * fetchWellnessReport       — runs the wellness report query
 * fetchWellnessFilterOptions — loads filter option sets (areas, residents, devices)
 */

import { apiFetch } from '../../../../services/api.js';

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

const getBaseUrl = () =>
    import.meta.env.VITE_DEVMODE === 'true'
        ? import.meta.env.VITE_DEVAPI
        : import.meta.env.VITE_API;

const getHeaders = () => ({
    'Content-Type': 'application/json',
    'x-amz-security-token': import.meta.env.VITE_AUTH_TOKEN,
});

// ---------------------------------------------------------------------------
// Fetch Wellness Report
// ---------------------------------------------------------------------------

/**
 * Runs the wellness report query.
 *
 * @param {object} opts
 * @param {number} opts.campusId
 * @param {string} opts.username
 * @param {string} opts.startDate   — ISO format (yyyy-mm-dd hh:mm:ss)
 * @param {string} opts.endDate     — ISO format
 * @param {string} opts.reportType  — 'check-in' | 'missed-check-in' | 'check-in-history' | 'rounds' | 'rounds-history'
 * @param {object} opts.filters     — filter selections
 * @returns {Promise<object>} — { records, summary, overlimit, url, ErrorCode }
 */
export async function fetchWellnessReport({ campusId, username, startDate, endDate, reportType, filters = {} }) {
    const body = {
        rep_type: reportType,
        username: username || '',
        campus_id: campusId,
        start_date: startDate,
        end_date: endDate,
        event_category_filter: '1',
        device_category_filter: filters.deviceCategories || '',
        area_type_filter: filters.areaTypes || '',
        resident_filter: filters.residents || '',
        resident_group_filter: filters.residentGroups || '',
        area_filter: filters.areas || '',
        area_group_filter: filters.areaGroups || '',
    };

    try {
        const resp = await fetch(getBaseUrl() + import.meta.env.VITE_MY_REPORTS_ENDPOINT, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(body),
        });

        if (resp.status === 503) return { ErrorCode: 11 };
        if (resp.status === 500) return { ErrorCode: 10 };
        if (resp.status === 403) return { ErrorCode: 12 };

        const raw = await resp.json();

        if (raw?.ErrorCode) return { ErrorCode: raw.ErrorCode };

        const data = raw?.Res || raw;

        if (data?.ErrorCode) return { ErrorCode: data.ErrorCode };

        return {
            records: data?.records || [],
            summary: data?.summary || [],
            overlimit: Boolean(data?.overlimit),
            url: data?.url || '',
        };
    } catch (err) {
        console.error('[wellnessApi] fetchWellnessReport error:', err);
        return { ErrorCode: 10 };
    }
}

// ---------------------------------------------------------------------------
// Fetch Filter Options
// ---------------------------------------------------------------------------

/**
 * Loads wellness-relevant filter option datasets.
 *
 * @param {number} campusId
 * @returns {Promise<object>}
 */
export async function fetchWellnessFilterOptions(campusId) {
    try {
        const portalFilters = await fetchPortalFilters(campusId);

        const residentsWithName = (portalFilters.residents || []).map((r) => ({
            ...r,
            resident_name: `${r.resident_first_name || ''} ${r.resident_last_name || ''}`.trim() || '(unknown)',
        }));

        return {
            areas: portalFilters.areas || [],
            areaGroups: portalFilters.areaGroups || [],
            areaTypes: portalFilters.areaTypes || [],
            residents: residentsWithName,
            residentGroups: portalFilters.residentGroups || [],
            deviceCategories: portalFilters.deviceCategories || [],
            users: portalFilters.users || [],
        };
    } catch (err) {
        console.error('[wellnessApi] fetchWellnessFilterOptions error:', err);
        return {};
    }
}

/**
 * Internal — calls getportalfilters and normalizes the result keys.
 */
async function fetchPortalFilters(campusId) {
    const body = {
        rep_type: 'getportalfilters',
        campus_id: campusId,
        device_category_filter: '1',
        area_type_filter: '1',
        user_filter: '1',
        resident_group_filter: '1',
        area_filter: '0',
        area_group_filter: '1',
        resident_filter: '1',
    };

    try {
        const resp = await fetch(getBaseUrl() + import.meta.env.VITE_MY_REPORTS_ENDPOINT, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(body),
        });

        const raw = await resp.json();
        const data = raw?.Res || raw;

        return {
            areas: data?.areas || [],
            areaGroups: data?.area_groups || [],
            areaTypes: data?.area_types || [],
            residents: data?.residents || [],
            residentGroups: data?.resident_groups || [],
            deviceCategories: data?.device_categories || [],
            users: data?.users || [],
        };
    } catch (err) {
        console.error('[wellnessApi] fetchPortalFilters error:', err);
        return {};
    }
}
